// Benefits administration (Phase B) — role-adaptive. Employees view plans and
// enrol/waive; HR manages the plan catalog and enrols employees.
import { useState, useEffect } from 'react';
import { Dialog, DialogTitle, DialogContent, DialogActions, TextField, FormControlLabel, Checkbox } from '@mui/material';
import { Plus, Trash2, Pencil, HeartPulse, Users } from 'lucide-react';
import { Card, SectionTitle, CenterSpinner, PrimaryBtn, OutlineBtn, Pill } from './ui';
import { EmptyState } from './components';
import { AG } from './theme/tokens';
import { api } from './api';
import { useAuth } from './auth';

const CATS = ['Health', 'Life', 'Dental', 'Vision', 'Retirement', 'Other'];
const money = (n) => `Rs ${Number(n || 0).toLocaleString('en-IN')}`;

export default function Benefits({ showToast }) {
  const { isHR } = useAuth();
  return isHR ? <BenefitsHR showToast={showToast} /> : <BenefitsEmployee showToast={showToast} />;
}

function BenefitsEmployee({ showToast }) {
  const [d, setD] = useState(null);
  const load = () => api.myBenefits().then(setD).catch(() => setD({ plans: [], premiums: { employee: 0, employer: 0 } }));
  useEffect(() => { load(); }, []);
  const set = async (planId, body) => { try { await api.setBenefit(planId, body); load(); } catch (e) { showToast?.(e.message); } };
  if (!d) return <CenterSpinner />;
  return (
    <div className="space-y-4 fade-up">
      <SectionTitle title="My Benefits" sub="Choose your coverage" />
      <Card style={{ padding: 16 }} className="flex items-center justify-between">
        <span style={{ fontSize: 13, color: AG.n500 }}>Your monthly premium</span>
        <span style={{ fontWeight: 700, color: AG.text }}>{money(d.premiums.employee)} <span style={{ fontSize: 12, color: AG.n400, fontWeight: 400 }}>+ {money(d.premiums.employer)} employer</span></span>
      </Card>
      {d.plans.length === 0 ? <Card><EmptyState icon={HeartPulse} title="No plans" desc="Benefit plans appear here when available." /></Card> : (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
          {d.plans.map((p) => (
            <Card key={p.id} style={{ padding: 16 }}>
              <div className="flex items-start justify-between" style={{ gap: 8 }}>
                <div>
                  <div style={{ fontWeight: 600, color: AG.text }}>{p.name} <Pill tone="info">{p.category}</Pill></div>
                  <div style={{ fontSize: 12, color: AG.n500, marginTop: 2 }}>{p.provider || '—'} · {money(p.employeeCost)}/mo (you)</div>
                </div>
                {p.status === 'Enrolled' ? <Pill tone="success">Enrolled · {p.coverage}</Pill> : p.status === 'Waived' ? <Pill tone="neutral">Waived</Pill> : null}
              </div>
              <div className="flex items-center gap-2" style={{ marginTop: 12 }}>
                {p.status === 'Enrolled' ? (
                  <>
                    <select value={p.coverage} onChange={(e) => set(p.id, { status: 'Enrolled', coverage: e.target.value })} className="ag-input" style={{ width: 120 }}>{['Self', 'Family'].map((c) => <option key={c}>{c}</option>)}</select>
                    <OutlineBtn onClick={() => set(p.id, { status: 'Waived' })}>Waive</OutlineBtn>
                  </>
                ) : (
                  <PrimaryBtn onClick={() => set(p.id, { status: 'Enrolled', coverage: 'Self' })}>Enrol</PrimaryBtn>
                )}
              </div>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}

const blankPlan = { name: '', provider: '', category: 'Health', employeeCost: 0, employerCost: 0, companyWide: false };

function BenefitsHR({ showToast }) {
  const [rows, setRows] = useState(null);
  const [people, setPeople] = useState([]);
  const [form, setForm] = useState(null);
  const [enrollFor, setEnrollFor] = useState(null);
  const [busy, setBusy] = useState(false);
  const load = () => api.benefitPlans().then(setRows).catch(() => setRows([]));
  useEffect(() => { load(); api.employees().then((r) => setPeople(Array.isArray(r) ? r : r.rows || [])).catch(() => setPeople([])); }, []);

  const save = async () => {
    if (!form.name.trim() || busy) return; setBusy(true);
    try { if (form.id) await api.updateBenefitPlan(form.id, form); else await api.createBenefitPlan(form); showToast?.('Plan saved'); setForm(null); load(); }
    catch (e) { showToast?.(e.message); } finally { setBusy(false); }
  };
  const remove = async (p) => { try { await api.deleteBenefitPlan(p.id); load(); } catch (e) { showToast?.(e.message); } };
  if (!rows) return <CenterSpinner />;
  return (
    <div className="space-y-4 fade-up">
      <div className="flex items-center justify-between" style={{ gap: 12, flexWrap: 'wrap' }}>
        <SectionTitle title="Benefits" sub="Plans, providers, premiums & enrollment" />
        <PrimaryBtn onClick={() => setForm({ ...blankPlan })}><Plus size={14} style={{ marginRight: 6 }} /> New plan</PrimaryBtn>
      </div>
      {rows.length === 0 ? <Card><EmptyState icon={HeartPulse} title="No plans" desc="Create benefit plans, then enrol employees." /></Card> : (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
          {rows.map((p) => (
            <Card key={p.id} style={{ padding: 16 }}>
              <div className="flex items-start justify-between" style={{ gap: 8 }}>
                <div>
                  <div style={{ fontWeight: 600, color: AG.text }}>{p.name} <Pill tone="info">{p.category}</Pill>{p.entityId ? null : <Pill tone="neutral">Company-wide</Pill>}</div>
                  <div style={{ fontSize: 12, color: AG.n500, marginTop: 2 }}>{p.provider || '—'} · you {money(p.employeeCost)} · employer {money(p.employerCost)}</div>
                </div>
                <div className="flex items-center gap-1">
                  <button onClick={() => setForm({ id: p.id, name: p.name, provider: p.provider || '', category: p.category, employeeCost: p.employeeCost, employerCost: p.employerCost })} style={{ background: 'none', border: 'none', cursor: 'pointer', color: AG.n400, padding: 4 }}><Pencil size={15} /></button>
                  <button onClick={() => remove(p)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: AG.n400, padding: 4 }}><Trash2 size={15} /></button>
                </div>
              </div>
              <div className="flex items-center justify-between" style={{ marginTop: 12 }}>
                <span style={{ fontSize: 12, color: AG.n500 }}>{p.enrolled} enrolled</span>
                <OutlineBtn onClick={() => setEnrollFor({ id: p.id, name: p.name })}><Users size={14} style={{ marginRight: 4 }} /> Enrol</OutlineBtn>
              </div>
            </Card>
          ))}
        </div>
      )}

      <Dialog open={!!form} onClose={() => !busy && setForm(null)} maxWidth="xs" fullWidth>
        <DialogTitle>{form?.id ? 'Edit plan' : 'New benefit plan'}</DialogTitle>
        <DialogContent>
          {form && (
            <div style={{ paddingTop: 6 }}>
              <TextField fullWidth size="small" label="Plan name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} sx={{ mb: 1.5 }} />
              <div className="grid grid-cols-2 gap-2" style={{ marginBottom: 8 }}>
                <TextField size="small" label="Provider" value={form.provider} onChange={(e) => setForm({ ...form, provider: e.target.value })} />
                <div><div style={{ fontSize: 12, fontWeight: 600, color: AG.n600 }}>Category</div><select value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} className="ag-input" style={{ marginTop: 4 }}>{CATS.map((c) => <option key={c}>{c}</option>)}</select></div>
              </div>
              <div className="grid grid-cols-2 gap-2">
                <TextField size="small" type="number" label="Employee cost/mo" value={form.employeeCost} onChange={(e) => setForm({ ...form, employeeCost: e.target.value })} />
                <TextField size="small" type="number" label="Employer cost/mo" value={form.employerCost} onChange={(e) => setForm({ ...form, employerCost: e.target.value })} />
              </div>
              {!form.id && <FormControlLabel control={<Checkbox checked={!!form.companyWide} onChange={(e) => setForm({ ...form, companyWide: e.target.checked })} />} label="Available company-wide" />}
            </div>
          )}
        </DialogContent>
        <DialogActions><OutlineBtn onClick={() => setForm(null)} disabled={busy}>Cancel</OutlineBtn><PrimaryBtn onClick={save} disabled={busy}>Save</PrimaryBtn></DialogActions>
      </Dialog>

      {enrollFor && <EnrollDialog plan={enrollFor} people={people} onClose={() => setEnrollFor(null)} onDone={(n) => { showToast?.(`${n} enrolled`); setEnrollFor(null); load(); }} showToast={showToast} />}
    </div>
  );
}

function EnrollDialog({ plan, people, onClose, onDone, showToast }) {
  const [sel, setSel] = useState({});
  const [coverage, setCoverage] = useState('Self');
  const [busy, setBusy] = useState(false);
  const enroll = async () => {
    const ids = Object.keys(sel).filter((k) => sel[k]);
    if (!ids.length || busy) return; setBusy(true);
    try { const r = await api.enrollBenefit(plan.id, ids, coverage); onDone(r.enrolled); } catch (e) { showToast?.(e.message); } finally { setBusy(false); }
  };
  return (
    <Dialog open onClose={() => !busy && onClose()} maxWidth="xs" fullWidth>
      <DialogTitle>Enrol · {plan.name}</DialogTitle>
      <DialogContent>
        <div style={{ marginBottom: 8 }}><label style={{ fontSize: 12, fontWeight: 600, color: AG.n600 }}>Coverage</label><select value={coverage} onChange={(e) => setCoverage(e.target.value)} className="ag-input" style={{ marginTop: 4 }}>{['Self', 'Family'].map((c) => <option key={c}>{c}</option>)}</select></div>
        <div style={{ maxHeight: 320, overflowY: 'auto' }}>
          {people.map((p) => <FormControlLabel key={p.id} control={<Checkbox checked={!!sel[p.id]} onChange={() => setSel((s) => ({ ...s, [p.id]: !s[p.id] }))} />} label={`${p.name}${p.dept ? ` · ${p.dept}` : ''}`} sx={{ display: 'flex' }} />)}
        </div>
      </DialogContent>
      <DialogActions><OutlineBtn onClick={onClose} disabled={busy}>Cancel</OutlineBtn><PrimaryBtn onClick={enroll} disabled={busy}>Enrol</PrimaryBtn></DialogActions>
    </Dialog>
  );
}
